import { FC, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import ListBox from "@ingka/list-box";
import Pill, { IconPill, IconPillProps } from "@ingka/pill";
import { breakpointL } from "@ingka/variables/design-tokens";
import chevronDownSmall from "@ingka/ssr-icon/paths/chevron-down-small";
import chevronUpSmall from "@ingka/ssr-icon/paths/chevron-up-small";

import useDropdown from "./useDropown";
import { SrOnly } from "../styles";

const StyledListBox = styled(ListBox)`
	margin-top: 0.5rem;
	z-index: 1000;
	@media (width < ${breakpointL}) {
		width: 70%;
		right: 2rem;
	}
`;

export interface PillDropdownProps {
	id: string;
	label?: string;
	value?: string;
	disabled?: boolean;
	ariaLabel: string;
	className?: string;
	ssrIcon?: any;
	iconOnly?: boolean;
	onChange: (...args: any[]) => any;
	children: ReactNode;
	size?: IconPillProps["size"];
}

const PillDropdown: FC<PillDropdownProps> = ({
	children,
	id,
	label,
	value,
	disabled,
	ariaLabel,
	className,
	ssrIcon,
	iconOnly,
	size = "small",
	onChange,
}) => {
	const { open, menuRef, buttonRef, handleToggle, handleClose } = useDropdown();
	const selectedValue = value ? [value] : undefined;

	const handleChange = useCallback(
		(args: any[]) => {
			onChange(args[0]);
			handleClose({ returnFocus: true });
		},
		[handleClose, onChange]
	);

	return (
		<>
			<StyledListBox
				id={`${id}-listbox`}
				containerRef={menuRef}
				open={open}
				onChange={handleChange}
				aria-multiselectable={undefined}
				value={selectedValue}
				aria-label={ariaLabel}
				{...(className ? { className } : {})}
				trigger={
					iconOnly ? (
						<IconPill
							id={`${id}-control`}
							data-testid={`${id}-control`}
							ref={buttonRef}
							onClick={handleToggle}
							ssrIcon={ssrIcon}
							ariaLabel={ariaLabel}
							aria-expanded={open}
							aria-haspopup="listbox"
							aria-pressed={undefined}
							size={size}
						/>
					) : (
						<Pill
							id={`${id}-control`}
							data-testid={`${id}-control`}
							ref={buttonRef}
							label={
								<>
									<SrOnly>{ariaLabel}</SrOnly>
									{label && label}
								</>
							}
							onClick={handleToggle}
							disabled={disabled}
							size={size}
							leadingIcon={ssrIcon}
							trailingIcon={open ? chevronUpSmall : chevronDownSmall}
							aria-expanded={open}
							aria-pressed={undefined}
						/>
					)
				}
			>
				{children}
			</StyledListBox>
		</>
	);
};

export default PillDropdown;
